/**
 * Registry → Finance Documents (Phase 86) — Founder/Admin only browsable
 * index of every document uploaded from any Finance ledger (Entries,
 * Invoices Ledger, Petty Cash) plus invoice compliance uploads (FIRC,
 * Credit Advice, Disposal Letter, Payment Confirmation). Read-only browse
 * view; uploads themselves happen inline on the Finance ledgers.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/dateFormat";
import { FileText, Link as LinkIcon, Search, Loader2 } from "lucide-react";

const SOURCE_LABEL = { entry: "Entry", invoice: "Invoice", petty_cash: "Petty Cash" };

export default function FinanceDocumentsTab() {
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = () => {
    setDocs(null);
    api.get("/finance/documents").then(({ data }) => setDocs(data || [])).catch((e) => { toast.error(formatApiError(e)); setDocs([]); });
  };
  useEffect(() => { load(); }, []);

  const projects = useMemo(() => Array.from(new Set((docs || []).map((d) => d.project_name).filter(Boolean))).sort(), [docs]);
  const clients = useMemo(() => Array.from(new Set((docs || []).map((d) => d.client_name).filter(Boolean))).sort(), [docs]);
  const types = useMemo(() => Array.from(new Set((docs || []).map((d) => d.doc_type).filter(Boolean))).sort(), [docs]);

  const filtered = useMemo(() => {
    let rows = docs || [];
    if (projectFilter !== "all") rows = rows.filter((d) => d.project_name === projectFilter);
    if (clientFilter !== "all") rows = rows.filter((d) => d.client_name === clientFilter);
    if (typeFilter !== "all") rows = rows.filter((d) => d.doc_type === typeFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter((d) => [d.project_name, d.client_name, d.doc_type, d.file_name, d.uploaded_by_name].some((v) => (v || "").toLowerCase().includes(needle)));
    }
    return rows;
  }, [docs, projectFilter, clientFilter, typeFilter, q]);

  return (
    <div data-testid="finance-documents-tab">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="rounded-sm pl-8 h-9 text-xs" data-testid="finance-docs-search" />
        </div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="rounded-sm h-9 text-xs w-40" data-testid="finance-docs-client-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="rounded-sm h-9 text-xs w-40" data-testid="finance-docs-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="rounded-sm h-9 text-xs w-40" data-testid="finance-docs-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All document types</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="card-flat overflow-hidden" data-testid="finance-documents-table">
        {docs === null ? (
          <div className="p-10 text-center text-xs text-slate-400 inline-flex items-center gap-2 w-full justify-center"><Loader2 size={12} className="animate-spin" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="finance-documents-empty">No documents match yet — uploads made from the Finance ledgers will show up here automatically.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {["Uploaded", "Document type", "File name", "Client", "Project", "Source", "Uploaded by", ""].map((h) => (
                    <th key={h} className="label-overline px-3 py-2 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50" data-testid={`finance-doc-row-${d.id}`}>
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{formatDate(d.uploaded_at)}</td>
                    <td className="px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5"><FileText size={11} className="text-[#F54800]" />{d.doc_type}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[160px] truncate" title={d.file_name || ""}>{d.file_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.client_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.project_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{SOURCE_LABEL[d.source_type] || d.source_type}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{d.uploaded_by_name || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <a href={d.file_url?.startsWith("http") ? d.file_url : `${process.env.REACT_APP_BACKEND_URL}${d.file_url}`} target="_blank" rel="noreferrer" className="text-[#F54800] inline-flex items-center gap-1 text-xs" data-testid={`finance-doc-view-${d.id}`}>
                        <LinkIcon size={11} /> view
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
